import {
  ArrowLeftOutlined,
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Avatar, theme, Typography } from "antd";
import { groupEvents } from "pages/api-clients/graph-search";
import {
  DeepLinkType,
  SnippetV1State,
  toExploreV1State,
} from "pages/api/deep-links/types";
import * as React from "react";
import { useQuery } from "react-query";
import { CompareWithIcon } from "src/components/CompareWithIcon";
import { DateRangeIcon } from "src/components/DateRangeIcon";
import { FilterByIcon } from "src/components/FilterByIcon";
import { Flex } from "src/components/Flex";
import { FormulaIcon } from "src/components/FormulaIcon";
import { GroupByIcon } from "src/components/GroupByIcon";
import { SelectIcon } from "src/components/SelectIcon";
import { useApplicationContext } from "src/hooks/useApplication";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { usePopupContext } from "src/hooks/usePopup";
import { useSnippets } from "src/hooks/useSnippets";
import { unserializeParams } from "src/search/lib/params-state-reducer";
import { SearchState } from "src/search/types";
import { useEntitySelectors } from "src/search/useEntitySelectors";
import { asKey, eq } from "src/utils/fields";
import { ClauseButton } from "./Clause";
import { ClauseMenu, useMenuEscape } from "./ClauseMenu";
import {
  ClauseMenuItem,
  Clauses,
  InputClause,
  QuestionInputOptions,
  UseQuestionInputClauses,
} from "./definitions";
import styles from "./question-input.module.css";
import { useSnippetMenu } from "./SnippetMenu";
import { useClauseColor } from "./useClauseColors";

type AddProps = {
  clauses: UseQuestionInputClauses["list"];
  onAdd: UseQuestionInputClauses["addClause"];
  onSetValue: UseQuestionInputClauses["setValue"];
  onMerge: UseQuestionInputClauses["mergeStateIntoClauses"];
  options: QuestionInputOptions;
  hideDisabled?: boolean; // Dashboards don't want to show unavailable options
  snippetTransformer?: (state: SearchState) => SearchState;
  value: SearchState;
};

export function AddClause(props: AddProps) {
  return (
    <div className={styles.addButton}>
      <ClauseButton
        focusKey="add"
        onMenuClose={() => {}}
        renderMenu={() => <AddMenu {...props} />}
      >
        +
      </ClauseButton>
    </div>
  );
}

function AddMenu(props: AddProps) {
  useMenuEscape();
  const toColor = useClauseColor() as Exclude<
    ReturnType<typeof useClauseColor>,
    string
  >;
  const { token } = theme.useToken();
  const popup = usePopupContext();
  const [filter, setFilter] = React.useState("");
  const snippets = useSnippets().list.filter((link) => {
    const json = link.json as SnippetV1State;
    return (
      json.params?.filters?.length === 1 &&
      eq({ target: "Activity", attribute: "Actor" }, json.params.filters.at(0))
    );
  });
  const handleSnippetChange = React.useCallback(
    (state?: SearchState) => {
      if (state)
        props.onMerge(
          props.snippetTransformer ? props.snippetTransformer(state) : state
        );
      popup.close();
    },
    [popup, props]
  );
  const snipMenu = useSnippetMenu({
    snippets,
    onChange: handleSnippetChange,
  });
  const [showSnippets, setShowSnippets] = React.useState(false);

  const counts = React.useMemo(() => {
    const f = props.clauses.filter.bind(props.clauses);
    return {
      selects: f((c) => c.type === Clauses.SELECT).length,
      groupBys: f((c) => c.type === Clauses.GROUP_BY).length,
      measureBys: f((c) => c.type === Clauses.MEASURE_BY).length,
      filterConditions: f((c) => c.type === Clauses.FILTER_CONDITION).length,
      searchTerms: f((c) => c.type === Clauses.SEARCH_TERM).length,
      dateRanges: f((c) => c.type === Clauses.DATE_RANGE).length,
      compareWiths: f((c) => c.type === Clauses.COMPARE_WITH).length,
      columnFormulas: f((c) => c.type === Clauses.FORMULA).length,
      columnHaving: f((c) => c.type === Clauses.HAVING).length,
    };
  }, [props.clauses]);

  const setSearchFromSuggestions = React.useCallback(
    (state: SearchState) => {
      props.onSetValue(state);
      popup.close();
    },
    [popup, props]
  );
  const suggestions = useSuggestions(
    props.value,
    filter,
    props.options,
    handleSnippetChange,
    setSearchFromSuggestions
  );

  const peopleFilters = useActivityPeopleFilters(
    props.onMerge,
    filter,
    props.options
  );

  let items: ClauseMenuItem[] = props.options.disableSuggestions
    ? []
    : [
        ...(suggestions.queryMatches.length > 0 &&
        !props.options.disableSuggestedQuestions
          ? [
              {
                key: "divider-shortcuts",
                aesthetic: true,
                label: (
                  <div
                    style={{
                      color: token.colorTextSecondary,
                      fontWeight: 500,
                      padding: 16,
                      paddingTop: 12,
                      paddingBottom: 0,
                    }}
                  >
                    Suggested questions
                  </div>
                ),
                keywords: [filter],
                onSelect() {},
              },
              ...suggestions.queryMatches,
            ]
          : []),
        ...(suggestions.bookmarkMatches.length > 0
          ? [
              {
                key: "divider-bookmarks",
                aesthetic: true,
                label: (
                  <div
                    style={{
                      color: token.colorTextSecondary,
                      fontWeight: 500,
                      padding: 16,
                      paddingTop: 12,
                      paddingBottom: 0,
                    }}
                  >
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Favorites
                    </Typography.Text>
                  </div>
                ),
                keywords: [filter],
                onSelect() {},
              },
              ...suggestions.bookmarkMatches,
            ]
          : []),
        {
          key: "divider-query-controls",
          aesthetic: true,
          label: (
            <div
              style={{
                color: token.colorTextSecondary,
                fontWeight: 500,
                padding: 16,
                paddingTop: 12,
                paddingBottom: 0,
              }}
            >
              Query controls
            </div>
          ),
          keywords: [filter],
          onSelect() {},
        },
        {
          key: Clauses.SELECT as string,
          label: (
            <Flex inline start gap={8}>
              <SelectIcon
                size={12}
                style={{
                  color: toColor({ type: Clauses.SELECT } as InputClause),
                }}
              />
              <span>Select</span>
            </Flex>
          ),
          keywords: ["find", "select", "entity", "type", "show"],
          disabled: props.options.disableSelect,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `select-${counts.selects}`,
                type: Clauses.SELECT,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        ...suggestions.selectMatches,
        {
          key: Clauses.FILTER_CONDITION,
          label: (
            <Flex inline start gap={8}>
              <FilterByIcon
                size={12}
                style={{
                  color: toColor({
                    type: Clauses.FILTER_CONDITION,
                  } as InputClause),
                }}
              />
              <span>Filter</span>
            </Flex>
          ),
          keywords: ["filter", "condition"],
          disabled: props.options.disableFilters,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `filter-condition-${counts.filterConditions}`,
                type: Clauses.FILTER_CONDITION,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        ...peopleFilters,
        ...suggestions.filterConditionMatches,
        {
          key: Clauses.GROUP_BY,
          label: (
            <Flex inline start gap={8}>
              <GroupByIcon
                size={12}
                style={{
                  color: toColor({ type: Clauses.GROUP_BY } as InputClause),
                }}
              />
              <span>Group</span>
            </Flex>
          ),
          keywords: ["group", "by", "on"],
          disabled: props.options.disableGroupBys,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `group-by-${counts.groupBys}`,
                type: Clauses.GROUP_BY,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        ...suggestions.groupByMatches,

        {
          key: Clauses.MEASURE_BY,
          label: (
            <Flex inline start gap={8}>
              <FormulaIcon
                size={12}
                style={{
                  color: toColor({ type: Clauses.MEASURE_BY } as InputClause),
                }}
              />
              <span>Measure</span>
            </Flex>
          ),
          keywords: ["measure", "formula"],
          disabled: props.options.disableMeasures,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `measure-by-${counts.measureBys}`,
                type: Clauses.MEASURE_BY,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        {
          key: Clauses.DATE_RANGE,
          label: (
            <Flex inline start gap={8}>
              <DateRangeIcon
                size={12}
                style={{
                  color: toColor({ type: Clauses.DATE_RANGE } as InputClause),
                }}
              />
              <span>Date range</span>
            </Flex>
          ),
          keywords: ["date", "range", "duration", "start", "end"],
          disabled: props.options.disableDateRange,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `date-range-${counts.dateRanges}`,
                type: Clauses.DATE_RANGE,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        {
          key: Clauses.FORMULA,
          label: (
            <Flex inline start gap={8}>
              <FormulaIcon
                size={12}
                style={{
                  color: toColor({ type: Clauses.FORMULA } as InputClause),
                }}
              />
              <span>Formula</span>
            </Flex>
          ),
          keywords: ["column", "formula"],
          disabled: props.options.disableFormula,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `column-formula-${counts.columnFormulas}`,
                type: Clauses.FORMULA,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        {
          key: Clauses.HAVING,
          label: (
            <Flex inline start gap={8}>
              <FilterByIcon
                size={12}
                style={{
                  color: toColor({ type: Clauses.HAVING } as InputClause),
                }}
              />
              <span>Having</span>
            </Flex>
          ),
          keywords: ["column", "having"],
          disabled: props.options.disableHaving,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `column-having-${counts.columnHaving}`,
                type: Clauses.HAVING,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        {
          key: Clauses.COMPARE_WITH,
          label: (
            <Flex inline start gap={8}>
              <CompareWithIcon
                size={12}
                style={{
                  color: toColor({ type: Clauses.COMPARE_WITH } as InputClause),
                }}
              />
              <span>Compare</span>
            </Flex>
          ),
          keywords: ["compare", "with", "previous", "before", "after"],
          disabled: props.options.disableCompareWith,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `compare-with-${counts.compareWiths}`,
                type: Clauses.COMPARE_WITH,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        {
          key: Clauses.SEARCH_TERM,
          label: (
            <Flex inline start gap={8}>
              <SearchOutlined
                size={12}
                style={{
                  color: toColor({ type: Clauses.SEARCH_TERM } as InputClause),
                }}
              />
              <span>Search</span>
            </Flex>
          ),
          keywords: ["search", "term", "keyword", "find"],
          disabled: props.options.disableSearchTerm,
          isOption: true,
          onSelect() {
            props.onAdd(
              {
                key: `search-${counts.searchTerms}`,
                type: Clauses.SEARCH_TERM,
                value: undefined,
              },
              "init"
            );
            popup.close();
          },
        },
        ...suggestions.searchMatches,
      ].filter(Boolean);

  // Deduplicate items based on their key (removing the match- prefix)
  items = [
    ...new Map(
      items.map((item) => [
        item.key.replace(/^match-/, ""), // Use the key without 'match-' prefix as unique identifier
        item,
      ])
    ).values(),
  ];

  if (!props.options.disableSnippets) {
    const snippetsToAdd =
      suggestions.snippetMatches.length > 0
        ? [
            {
              key: "divider-snippets",
              aesthetic: true,
              label: (
                <div
                  style={{
                    color: token.colorTextSecondary,
                    fontWeight: 500,
                    padding: 16,
                    paddingTop: 12,
                    paddingBottom: 0,
                  }}
                >
                  Query snippets
                </div>
              ),
              keywords: [filter],
              onSelect() {},
            },
            ...suggestions.snippetMatches,
          ]
        : [];

    items.push(...snippetsToAdd);
  }

  // Deduplicate again after adding snippets
  items = [
    ...new Map(
      items.map((item) => [
        item.key.replace(/^match-/, ""), // Use the key without 'match-' prefix as unique identifier
        item,
      ])
    ).values(),
  ];

  if (props.options.addFromTeamSnippet)
    items.push({
      key: "team-snippet",
      label: (
        <span>
          <TeamOutlined /> Team filtering
        </span>
      ),
      isOption: true,
      disabled: false,
      onSelect() {
        setShowSnippets(true);
      },
    });

  items = items
    .filter((item) => {
      const terms = (filter || "").toLowerCase().split(" ").filter(Boolean);
      if (terms.length === 0) return true;

      const itemKeywords = (item.keywords || []).map((str) =>
        str.toLowerCase()
      );
      const labelKeywords = (typeof item.label === "string" ? item.label : "")
        .toLowerCase()
        .split(" ")
        .filter(Boolean);
      return terms.every((term) =>
        [...itemKeywords, ...labelKeywords].some((keyword) =>
          keyword.includes(term)
        )
      );
    })
    .filter((item) => !props.hideDisabled || !item.disabled);

  items = items
    .map((item, index) => {
      if (item.aesthetic) {
        if (index === items.length - 1) return undefined; // empty groups
        if (items[index + 1]?.aesthetic) return undefined; // redundant dividers
      }
      return item;
    })
    .filter(Boolean) as typeof items;

  return (
    <ClauseMenu
      filterable
      onFilterChange={showSnippets ? snipMenu.setFilter : setFilter}
      items={showSnippets ? snipMenu.items : items}
      filterSuggestions={
        showSnippets
          ? snipMenu.prefixes.length > 0
            ? [{ items: snipMenu.prefixes }]
            : []
          : undefined
      }
    />
  );
}

// we do the magic here.
function useSuggestions(
  searchState: SearchState,
  filter: string,
  options: QuestionInputOptions,
  handleSnippetChange: (state?: SearchState) => void,
  onSetValue: (state: SearchState) => void
): {
  bookmarkMatches: ClauseMenuItem[];
  filterConditionMatches: ClauseMenuItem[];
  groupByMatches: ClauseMenuItem[];
  queryMatches: ClauseMenuItem[];
  searchMatches: ClauseMenuItem[];
  selectMatches: ClauseMenuItem[];
  snippetMatches: ClauseMenuItem[];
} {
  // There are three types of suggestions:
  //  1. Snippet, found via filtering
  //  2. A possible best match for each control type
  //  3. Contextual suggestions, based on the search state.
  // The second two should also consider the filter value

  const terms = filter
    .split(" ")
    .filter(Boolean)
    .map((str) => str.toLowerCase());

  const snippets = useSnippets();
  const snippetMatches: ClauseMenuItem[] = options.disableSuggestions
    ? []
    : (terms.length > 1 || terms[0]?.length > 1
        ? snippets.list.filter((snippet) => {
            const { name, description, json } = snippet;
            const { tags, params } = json as SnippetV1State;
            const searchable = JSON.stringify([
              name,
              description,
              tags,
              params?.select,
              params?.groupBys,
              params?.aggregations,
              params?.filters,
            ]).toLowerCase();
            return terms.every((term) => searchable.includes(term));
          })
        : []
      )
        .map((link) => {
          const state = (link.json || {}) as SnippetV1State;
          return {
            ...clauseMenuItemMatch(
              state.params,
              link.name,
              handleSnippetChange
            ),
            keywords: [filter],
          };
        })
        .slice(0, 10);

  const bookmarks = useDeepLinks(DeepLinkType.EXPLORE).links.data?.filter(
    (link) => link.isBookmarked
  );
  const bookmarkMatches: ClauseMenuItem[] = options.disableSuggestions
    ? []
    : (
        (terms.length > 1 || terms[0]?.length > 1
          ? bookmarks?.filter((bookmark) => {
              const { name, description } = bookmark;
              const { serialized } = toExploreV1State(bookmark.state);
              const state = unserializeParams(serialized);
              const searchable = JSON.stringify([
                name,
                description,
                state.select,
                state.filters,
                state.groupBys,
                state.aggregations,
              ]).toLowerCase();
              return terms.every((term) => searchable.includes(term));
            })
          : []) || []
      )
        .map((bookmark) => {
          const { serialized } = toExploreV1State(bookmark.state);
          const state = unserializeParams(serialized);
          return {
            ...clauseMenuItemMatch(state, bookmark.name, onSetValue, true),
            keywords: [filter],
          };
        })
        .slice(0, 10);

  const { filters: filterFields } = useEntitySelectors({ state: searchState });
  const potTargets = [...new Set(filterFields.map((f) => f.target))];
  const potTarget =
    (
      terms
        .map((term) => {
          const matches = potTargets.filter((target) =>
            target.toLowerCase().startsWith(term)
          );
          matches.sort((a, b) => a.length - b.length);
          return matches.length > 0 ? matches[0] : undefined;
        })
        .filter(Boolean) as string[]
    )[0] ||
    searchState.select?.at(0) ||
    "";

  const potFilters = options.disableFilters
    ? []
    : potTarget
    ? filterFields.filter((f) => f.target === potTarget)
    : [];
  const potAttributes = potFilters.map((f) => f.attribute);
  const potAttribute =
    (
      terms
        .map((term) => {
          if (potTarget.toLowerCase().startsWith(term)) return undefined;
          const matches = potAttributes.filter((attribute) =>
            attribute.toLowerCase().includes(term)
          );
          matches.sort((a, b) => a.length - b.length);
          return matches.length > 0 ? matches[0] : undefined;
        })
        .filter(Boolean) as string[]
    )[0] ||
    potAttributes[0] ||
    "";

  const parts = hasParts(searchState);
  const searchMatches =
    !options.disableSearchTerm && filter
      ? [
          clauseMenuItemMatch(
            { search: filter },
            `Search term: ${filter}`,
            handleSnippetChange,
            "left"
          ),
        ]
      : [];
  const selectMatches =
    !options.disableSelect && filter && potTarget && !parts.hasSelect
      ? [
          clauseMenuItemMatch(
            { select: [potTarget] },
            `Select: ${potTarget}`,
            handleSnippetChange,
            "left"
          ),
        ]
      : [];
  const groupByMatches =
    !options.disableGroupBys &&
    filter &&
    potTarget &&
    potAttribute &&
    !parts.hasGroupBys
      ? [
          clauseMenuItemMatch(
            { groupBys: [{ target: potTarget, attribute: potAttribute }] },
            `Group by: ${potTarget}.${potAttribute}`,
            handleSnippetChange,
            "left"
          ),
        ]
      : [];
  const filterConditionMatches =
    !options.disableFilters && filter && potTarget && potAttribute
      ? [
          clauseMenuItemMatch(
            {
              filters: [
                {
                  key: asKey({ target: potTarget, attribute: potAttribute }),
                  target: potTarget,
                  attribute: potAttribute,
                  comparison: { "NOT IN": ["__VZBL::null__"] },
                },
              ],
            },
            `Filter on: ${potTarget}.${potAttribute}`,
            handleSnippetChange,
            "left"
          ),
        ]
      : [];

  const queryMatches: ClauseMenuItem[] = [];
  if (!options.disableSuggestions)
    switch (potTarget) {
      case "CicdPipeline": {
        queryMatches.push(...cicdPipelineMatches(searchState, onSetValue));
        break;
      }
      case "Commit": {
        queryMatches.push(...commitMatches(searchState, onSetValue));
        break;
      }
      case "Event": {
        queryMatches.push(...activityMatches(searchState, onSetValue));
        break;
      }
      case "Issue": {
        queryMatches.push(...issueMatches(searchState, onSetValue));
        break;
      }
      case "Person": {
        queryMatches.push(...personMatches(searchState, onSetValue));
        break;
      }
      case "Team": {
        queryMatches.push(...teamMatches(searchState, onSetValue));
        break;
      }
      default: {
        queryMatches.push(...startingMatches(searchState, onSetValue));
      }
    }

  // also pull from examples to suggest queries
  const examples = useDeepLinks(DeepLinkType.EXAMPLE).links.data || [];
  if (!options.disableSuggestions && terms.some((t) => t.trim().length > 0)) {
    const relevantExamples = potTarget
      ? examples.filter((example) =>
          example.json.params.select.includes(potTarget)
        )
      : examples;

    queryMatches.push(
      ...relevantExamples.map((example) => {
        return {
          ...clauseMenuItemMatch(
            example.json.params,
            example.name,
            onSetValue,
            true
          ),
          // keywords: [filter],
        };
      })
    );
  }

  return {
    bookmarkMatches,
    filterConditionMatches,
    groupByMatches,
    queryMatches,
    searchMatches,
    selectMatches,
    snippetMatches,
  };
}

function cicdPipelineMatches(
  _state: Partial<SearchState>,
  handleChange: (state: SearchState) => void
): ClauseMenuItem[] {
  return [
    clauseMenuItemMatch(
      {
        select: ["CicdPipeline"],
        groupBys: [
          { target: "Activity", attribute: "Team" },
          { target: "Repository", attribute: "name" },
        ],
        duration: "P7D",
      },
      "Deployments by team and repo - P7D",
      handleChange,
      true
    ),
    clauseMenuItemMatch(
      {
        select: ["CicdPipeline"],
        groupBys: [
          { target: "Repository", attribute: "name" },
          { target: "CicdPipeline", attribute: "state" },
        ],
        duration: "P3D",
      },
      "Recent deployments by status - P3D",
      handleChange,
      true
    ),
  ];
}
function commitMatches(
  _state: Partial<SearchState>,
  handleChange: (state: SearchState) => void
): ClauseMenuItem[] {
  return [
    clauseMenuItemMatch(
      {
        select: ["Commit"],
        filters: [
          {
            key: "shadow",
            target: "Issue",
            attribute: "issueKey",
            comparison: { "NOT IN": ["__VZBL::null__"] },
          },
        ],
        duration: "P7D",
      },
      "Commits without an issue - P7D",
      handleChange,
      true
    ),
    clauseMenuItemMatch(
      {
        select: ["Commit"],
        filters: [
          {
            key: "risk",
            target: "Commit",
            attribute: "vzblRiskScore",
            comparison: { WITHIN: ["7"] },
          },
        ],
        duration: "P7D",
      },
      "Risky commits - P7D",
      handleChange,
      true
    ),
    clauseMenuItemMatch(
      {
        select: ["Commit"],
        groupBys: [
          { target: "Issue", attribute: "Activity.Day" },
          { target: "Commit", attribute: "Repository" },
        ],
        duration: "P7D",
      },
      "Commits by repo and day - P7D",
      handleChange,
      true
    ),
  ];
}
function issueMatches(
  _state: Partial<SearchState>,
  handleChange: (state: SearchState) => void
): ClauseMenuItem[] {
  return [
    clauseMenuItemMatch(
      {
        select: ["Issue"],
        groupBys: [
          { target: "Issue", attribute: "issueType" },
          { target: "Issue", attribute: "Activity.Week" },
        ],
        filters: [
          {
            key: "issue.status",
            target: "Issue",
            attribute: "status",
            comparison: { IN: ["completed"] },
          },
        ],
        duration: "P30D",
      },
      "Completed issues by type and week - P30D",
      handleChange,
      true
    ),
    clauseMenuItemMatch(
      { select: ["Issue"], duration: "P7D" },
      "Issue activity - P7D",
      handleChange,
      true
    ),
    clauseMenuItemMatch(
      {
        select: ["Issue"],
        groupBys: [
          { target: "Issue", attribute: "vzblClassification" },
          { target: "Issue", attribute: "vzblCycleTime" },
        ],
        duration: "P30D",
      },
      "Issues by classification and cycle-time - P30D",
      handleChange,
      true
    ),
  ];
}
function personMatches(
  _state: Partial<SearchState>,
  handleChange: (state: SearchState) => void
): ClauseMenuItem[] {
  return [
    clauseMenuItemMatch(
      {
        select: ["Team"],
        groupBys: [{ target: "Person", attribute: "name" }],
        filters: [
          {
            key: "not-bot",
            target: "Person",
            attribute: "isBot",
            comparison: { IN: ["false", "__VZBL::null__"] },
          },
          {
            key: "has-name",
            target: "Person",
            attribute: "name",
            comparison: { "NOT IN": ["__VZBL::null__"] },
          },
        ],
      },
      "Team count by person",
      handleChange,
      true
    ),
    clauseMenuItemMatch(
      {
        select: ["Commit"],
        groupBys: [{ target: "Commit", attribute: "CreatedBy" }],
        duration: "P1D",
      },
      "Commit activity by person - P1D",
      handleChange,
      true
    ),
  ];
}
function teamMatches(
  _state: Partial<SearchState>,
  handleChange: (state: SearchState) => void
): ClauseMenuItem[] {
  return [
    clauseMenuItemMatch(
      {
        select: ["CicdPipeline"],
        groupBys: [{ target: "Activity", attribute: "Team" }],
        duration: "P7D",
      },
      "Deployments by team - P7D",
      handleChange,
      true
    ),
    clauseMenuItemMatch(
      {
        select: ["Commit"],
        groupBys: [
          { target: "Activity", attribute: "Team" },
          { target: "Commit", attribute: "Repository" },
        ],
        duration: "P7D",
      },
      "Commit activity by team by repo - P7D",
      handleChange,
      true
    ),
  ];
}
function activityMatches(
  _state: Partial<SearchState>,
  handleChange: (state: SearchState) => void
): ClauseMenuItem[] {
  return [
    clauseMenuItemMatch(
      {
        select: ["Event"],
        groupBys: [{ target: "Event", attribute: "EntityType" }],
        duration: "P3D",
      },
      "Activity by action - P3D",
      handleChange,
      true
    ),
    clauseMenuItemMatch(
      {
        select: ["Event"],
        groupBys: [
          { target: "Event", attribute: "EntityType" },
          { target: "Activity", attribute: "Team" },
        ],
        duration: "P7D",
      },
      "Activity by entity and team - P7D",
      handleChange,
      true
    ),
  ];
}

function startingMatches(
  state: Partial<SearchState>,
  handleChange: (state: SearchState) => void
): ClauseMenuItem[] {
  return [
    issueMatches(state, handleChange)[0],
    cicdPipelineMatches(state, handleChange)[0],
  ].filter(Boolean);
}

function clauseMenuItemMatch(
  state: Partial<SearchState>,
  title: string,
  handleChange: (state: SearchState) => void,
  replacing?: boolean | "left"
): ClauseMenuItem {
  return {
    key: `match-${title}`,
    keywords: `${title}`.toLowerCase().split(" ").filter(Boolean),
    isOption: true,
    label: <LinkWithIcons replacing={replacing} state={state} title={title} />,
    onSelect() {
      handleChange(state as SearchState);
    },
  };
}

function LinkWithIcons({
  state,
  title,
  replacing,
}: {
  state: Partial<SearchState>;
  title: string;
  replacing?: boolean | "left";
}) {
  const { token } = theme.useToken();
  const parts = hasParts(state);
  const icons = [
    parts.hasSelect && <SelectIcon key="select" size={10} />,
    parts.hasGroupBys && <GroupByIcon key="group-by" size={10} />,
    parts.hasFilters && <FilterByIcon key="filter" size={10} />,
    parts.hasDate && <DateRangeIcon key="date-range" size={10} />,
    (parts.hasFormulas || parts.hasMeasures) && (
      <FormulaIcon key="formula" size={10} />
    ),
    parts.hasCompare && <CompareWithIcon key="compare" size={10} />,
    parts.hasSearch && <SearchOutlined key="search" size={10} />,
  ].filter(Boolean);
  return (
    <Flex start gap={8}>
      {replacing === true ? (
        <ArrowLeftOutlined rotate={45} size={12} style={{ opacity: 0.5 }} />
      ) : replacing === "left" ? (
        icons[0]
      ) : (
        <PlusOutlined size={12} style={{ opacity: 0.5 }} />
      )}
      <span style={{ flex: 1 }} className="trunc">
        {title}
      </span>
      {!replacing && (
        <div
          style={{
            display: "inline-flex",
            gap: 2,
            color: token.colorFill,
          }}
        >
          {icons}
        </div>
      )}
    </Flex>
  );
}

function hasParts(state?: Partial<SearchState>) {
  return {
    hasCompare: Boolean(state?.compareWith),
    hasDate: state?.duration || state?.startDate || state?.endDate,
    hasFilters: (state?.filters?.length || 0) > 0,
    hasFormulas: (state?.formulas?.length || 0) > 0,
    hasGroupBys: (state?.groupBys?.length || 0) > 0,
    hasMeasures: (state?.aggregations?.length || 0) > 0,
    hasSearch: (state?.search || "").trim().length > 0,
    hasSelect: (state?.select?.length || 0) > 0,
  };
}

function useActivityPeopleFilters(
  onSelect: (state: SearchState) => void,
  filter: string,
  options: QuestionInputOptions
) {
  const orgId = useApplicationContext().org_id!;
  const actors = useQuery(["activity-actors", { orgId }], async () =>
    groupEvents(
      {
        groupBys: [{ target: "Event", attribute: "Actor" }],
      },
      orgId,
      {}
    )
  );
  const teams = useQuery(["activity-teams", { orgId }], async () =>
    groupEvents(
      {
        groupBys: [{ target: "Event", attribute: "Team" }],
      },
      orgId,
      {}
    )
  );

  const popup = usePopupContext();
  if (filter.trim().length < 2) return [];
  return [
    ...(actors.data?.data || [])
      .filter(
        (actor: any) =>
          typeof actor === "object" &&
          typeof actor["Event.Actor"] === "object" &&
          ["false", "__VZBL::null__"].includes(actor["Event.Actor"].isBot)
      )
      .map((actor: any) => ({
        key: `${Clauses.FILTER_CONDITION}-${actor["Event.Actor"].uuid}`,
        keywords: [
          "actor",
          "filter",
          "condition",
          actor["Event.Actor"].name.toLowerCase().includes(filter.toLowerCase())
            ? filter
            : "",
        ],
        disabled: options.disableFilters,
        isOptions: true,
        label: (
          <Flex centeredY gap={6} style={{ padding: "4px 16px" }}>
            <Avatar
              style={{ height: 24, width: 24 }}
              src={actor["Event.Actor"].avatarUrl || undefined}
            >
              {actor["Event.Actor"].name}
            </Avatar>
            <span style={{ flex: 1 }}>{actor["Event.Actor"].name}</span>
          </Flex>
        ),
        onSelect() {
          onSelect({
            orgId: "",
            filters: [
              {
                key: "filter-actor",
                target: "Activity",
                attribute: "Actor",
                comparison: { IN: [actor["Event.Actor"].name] },
              },
            ],
          });
          popup.close();
        },
      })),
    ...(teams.data?.data || [])
      .filter(
        (team: any) =>
          typeof team === "object" && typeof team["Event.Team"] === "object"
      )
      .map((team: any) => ({
        key: `${Clauses.FILTER_CONDITION}-${team["Event.Team"].uuid}`,
        keywords: [
          "team",
          "filter",
          "condition",
          team["Event.Team"].name.toLowerCase().includes(filter.toLowerCase())
            ? filter
            : "",
        ],
        disabled: options.disableFilters,
        isOptions: true,
        label: (
          <Flex centeredY gap={6} style={{ padding: "4px 16px" }}>
            <Avatar
              style={{ height: 24, width: 24 }}
              src={team["Event.Team"].avatarUrl || undefined}
            >
              {team["Event.Team"].name}
            </Avatar>
            <span style={{ flex: 1 }}>{team["Event.Team"].name}</span>
          </Flex>
        ),
        onSelect() {
          onSelect({
            orgId: "",
            filters: [
              {
                key: "filter-team",
                target: "Activity",
                attribute: "Team",
                comparison: { IN: [team["Event.Team"].name] },
              },
            ],
          });
          popup.close();
        },
      })),
  ];
}
